import { useChangeEmail } from "hooks/useChangeEmail";
import { LoadingButton } from "@mui/lab";
import { Box, Button, FormControl, InputLabel, OutlinedInput, Stack, TextField, Typography, useTheme, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, InputAdornment } from "@mui/material";
import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { emailRegex } from "constants/regex";
import { UserProfileViewContext } from "context/userProfile/UserProfileViewContext";
import { UserRoles } from "constants/userRoles";
import { truncateString } from "helpers/stringHelper";
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { CancelButton, SaveButton, ChangeEmailButton, StyledFormControl, ErrorTypography, DialogCancelButton } from './ChangeEmailSection.styles';

export const ChangeEmailSection = (props: { email: string }) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const [changingEmail, setChangingEmail] = useState(false);
    const [newEmail, setNewEmail] = useState(props.email);
    const [emailError, setEmailError] = useState('');
    const [showPasswordDialog, setShowPasswordDialog] = useState(false);
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [passwordError, setPasswordError] = useState('');
    const { changeEmail, changeEmailPending } = useChangeEmail();
    const { refreshProfile, isProfileOfLoggedUser, user } = useContext(UserProfileViewContext);

    const handleSave = async () => {
        if (validateEmail()) {
            setShowPasswordDialog(true);
        }
    }

    const handlePasswordConfirm = async () => {
        if (!password) {
            setPasswordError(t('loginPage.errors.passwordRequired'));
            return;
        }
        setPasswordError('');

        const resp = await changeEmail({ 
            newEmail, 
            userId: isProfileOfLoggedUser ? undefined : user?.id,
            password 
        });
        
        if (resp?.success) {
            setChangingEmail(false);
            setShowPasswordDialog(false);
            setPassword('');
            refreshProfile();
        }
    }

    const isValidEmail = (email: string): boolean => {
        return emailRegex.test(email);
    };

    const validateEmail = () => {
        if(!isValidEmail(newEmail)) {
            setEmailError(t('profile.invalidEmail'));
            return false;
        }

        setEmailError('');
        return true;
    }

    const handleClickShowPassword = () => setShowPassword(!showPassword);
    const handleMouseDownPassword = (event: any) => event.preventDefault();
    
    return <>
        <Stack width='100%' direction={'row'} display={'flex'}>
            {changingEmail
            ? <>
                <Box width='50%'>
                    <FormControl variant="outlined">
                        <TextField id="newEmail" label={t('general.newEmail')} variant="standard" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} />
                    </FormControl>
                    {emailError && <Typography color={'red'}>{emailError}</Typography>}
                </Box>
                <Box width='50%'>
                    <CancelButton variant='outlined' color='primary' onClick={() => setChangingEmail(false)}>{t('profile.cancel')}</CancelButton>
                    {(user?.role !== UserRoles.ADMIN || isProfileOfLoggedUser) && <SaveButton loading={changeEmailPending} variant='outlined' color='primary' onClick={handleSave}>{t('profile.save')}</SaveButton>}
                </Box>
            </>
            : <>
                <Stack width='50%'>
                    <Typography variant="caption" color={theme.palette.text.secondary}>{t('profile.email')}</Typography>
                    <Typography fontWeight={'500'}>{truncateString(props.email, 22)}</Typography>
                </Stack>
                <ChangeEmailButton variant='outlined' color='info' onClick={() => setChangingEmail(true)}>{t('profile.changeEmail')}</ChangeEmailButton>                        
            </>}
        </Stack>

        <Dialog open={showPasswordDialog} onClose={() => setShowPasswordDialog(false)}>
            <DialogTitle>{t('profile.confirmPassword')}</DialogTitle>
            <DialogContent>
                <StyledFormControl variant="outlined">
                    <InputLabel htmlFor="password">{t('general.password')}</InputLabel>
                    <OutlinedInput
                        id="password"
                        label={t('general.password')}
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        endAdornment={
                            <InputAdornment position="end">
                                <IconButton onClick={handleClickShowPassword} onMouseDown={handleMouseDownPassword} edge="end">
                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                            </InputAdornment>
                        }
                    />
                    {passwordError && <ErrorTypography color={'red'}>{passwordError}</ErrorTypography>}
                </StyledFormControl>
            </DialogContent>
            <DialogActions>
                <DialogCancelButton onClick={() => setShowPasswordDialog(false)} variant="outlined" color="primary">
                    {t('profile.cancel')}
                </DialogCancelButton>
                <LoadingButton loading={changeEmailPending} onClick={handlePasswordConfirm} variant="outlined" color="primary">
                    {t('profile.confirm')}
                </LoadingButton>
            </DialogActions>
        </Dialog>
    </>
}